if (!customElements.get('rdb-offers')) {
  customElements.define('rdb-offers', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      const on = (element, type, listener, options = {}) => element.addEventListener(type, listener, { ...options, signal: this.abort.signal });
      this.viewport = this.querySelector('.rdb-offers__viewport');
      this.direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      this.list = this.querySelector('.rdb-offers__list');
      this.cards = [...this.list.children];
      this.info = this.querySelector('.rdb-offers__info');
      this.motion = this.querySelector('[data-offers-motion]');
      this.status = this.querySelector('.rdb-offers__status');
      if (!this.cards.length) { this.hidden = true; return; }
      this.media = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.media.matches;
      this.phase = 0;
      this.speed = 0;
      this.inertia = 0;
      this.resumeAt = 0;
      this.visible = false;
      this.hovered = false;
      this.suppressClickUntil = 0;
      this.frame = time => this.tick(time);
      const copySet = () => this.cards.map(card => {
        const copy = card.cloneNode(true);
        copy.dataset.offerClone = '';
        copy.setAttribute('aria-hidden', 'true');
        copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('[id]').forEach(e => e.removeAttribute('id'));
        copy.querySelectorAll('a, button, [tabindex]').forEach(e => e.tabIndex = -1);
        return copy;
      });
      this.list.prepend(...copySet());
      this.list.append(...copySet());
      this.querySelectorAll('[data-copy-code]').forEach(e => { e.hidden = false; });
      this.list.querySelectorAll('img').forEach(e => { e.draggable = false; });
      this.classList.add('is-looping');
      this.querySelector('.rdb-offers__controls').hidden = false;
      this.setMotionLabel();
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.viewport);
      this.intersection = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.wake(); else this.sleep();
      }, { threshold: 0 });
      this.intersection.observe(this.viewport);
      on(document, 'visibilitychange', () => {
        this.checkExpiry();
        if (document.hidden) this.sleep(); else this.wake();
      });
      on(this.media, 'change', () => {
        if (this.media.matches) { this.paused = true; this.inertia = 0; this.tween = null; }
        this.setMotionLabel(); this.wake();
      });
      on(this.motion, 'click', () => {
        this.paused = !this.paused;
        this.speed = 0; this.inertia = 0; this.resumeAt = 0;
        this.setMotionLabel(); this.wake();
      });
      on(this.querySelector('[data-offers-prev]'), 'click', () => this.step(-1));
      on(this.querySelector('[data-offers-next]'), 'click', () => this.step(1));
      on(this.info, 'toggle', () => { this.inertia = 0; this.tween = null; this.wake(); });
      on(document, 'pointerdown', event => {
        if (this.info.open && !this.info.contains(event.target)) this.info.open = false;
      });
      on(this, 'keydown', event => {
        if (event.key === 'Escape' && this.info.open) {
          this.info.open = false;
          this.info.querySelector('summary').focus();
          event.preventDefault();
        }
      });
      on(this.viewport, 'keydown', event => {
        if (event.target !== this.viewport) return;
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        if (event.key === 'Home' || event.key === 'End') {
          this.phase = event.key === 'Home' ? 0 : (this.cards.length - 1) * this.stepWidth;
          this.tween = null; this.inertia = 0; this.draw();
        } else this.step((event.key === 'ArrowRight' ? 1 : -1) * this.direction);
      });
      on(this.viewport, 'pointerenter', event => {
        if (event.pointerType === 'mouse') { this.hovered = true; this.speed = 0; this.wake(); }
      });
      on(this.viewport, 'pointerleave', event => {
        if (event.pointerType === 'mouse') { this.hovered = false; this.wake(); }
      });
      on(this.viewport, 'focusin', event => {
        this.speed = 0; this.inertia = 0; this.tween = null;
        const card = event.target.closest('.rdb-offers__ticket');
        const index = this.cards.indexOf(card);
        if (index >= 0 && event.target.matches(':focus-visible')) {
          const left = index * this.stepWidth - this.phase;
          if (left < 0 || left + this.cardWidth > this.viewport.clientWidth) {
            this.phase = index * this.stepWidth; this.draw();
          }
        }
        this.wake();
      });
      on(this.viewport, 'focusout', () => queueMicrotask(() => this.wake()));
      on(this.viewport, 'pointerdown', event => this.pointerDown(event));
      on(this.viewport, 'pointermove', event => this.pointerMove(event), { passive: false });
      on(window, 'pointerup', event => this.pointerEnd(event));
      on(window, 'pointercancel', event => this.pointerEnd(event, true));
      on(this.viewport, 'lostpointercapture', event => {
        // A touch starts captured by its card; transferring it to the track is not cancellation.
        if (event.target === this.viewport) this.pointerEnd(event, true);
      });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this.viewport, 'selectstart', event => { if (this.drag?.locked) event.preventDefault(); });
      on(this.viewport, 'wheel', event => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
        event.preventDefault();
        this.phase += event.deltaX * this.direction;
        this.speed = 0; this.inertia = 0; this.tween = null;
        this.resumeAt = performance.now() + 1800;
        this.draw(); this.wake();
      }, { passive: false });
      on(this.viewport, 'click', event => {
        if (performance.now() < this.suppressClickUntil) { event.preventDefault(); event.stopImmediatePropagation(); return; }
        const button = event.target.closest('[data-copy-code]');
        if (button) this.copyCode(button);
      }, { capture: true });
      this.timer = setInterval(() => this.checkExpiry(), 60000);
      this.measure(); this.checkExpiry();
    }

    measure() {
      if (!this.viewport.clientWidth) return;
      if (this.measuredWidth === this.viewport.clientWidth) return;
      this.measuredWidth = this.viewport.clientWidth;
      const fraction = this.cycle ? this.phase / this.cycle : 0;
      this.cardWidth = innerWidth < 750 ? 156 : Math.max(156, (this.viewport.clientWidth - 60) / 6);
      this.style.setProperty('--offer-width', `${this.cardWidth}px`);
      this.cardWidth = this.cards[0].getBoundingClientRect().width;
      this.stepWidth = this.cardWidth + 12;
      this.cycle = this.stepWidth * this.cards.length;
      this.phase = fraction * this.cycle;
      this.inertia = 0; this.tween = null; this.drag = null;
      this.classList.remove('is-dragging');
      this.draw(); this.wake();
    }

    draw() {
      if (!this.cycle) return;
      this.phase = ((this.phase % this.cycle) + this.cycle) % this.cycle;
      this.list.style.transform = `translate3d(${(-this.cycle - this.phase) * this.direction}px, 0, 0)`;
    }

    keyboardFocus() {
      const active = document.activeElement;
      return this.viewport.contains(active) && active.matches(':focus-visible');
    }

    tick(time) {
      this.raf = 0;
      if (!this.isConnected || !this.visible || document.hidden || this.hidden) { this.sleep(); return; }
      const dt = this.lastTime ? Math.min(time - this.lastTime, 40) : 0;
      this.lastTime = time;
      const blocked = this.paused || this.hovered || this.keyboardFocus() || this.info.open || this.drag;
      let moving = false;
      if (this.tween) {
        const progress = Math.min(1, (time - this.tween.start) / 360);
        this.phase = this.tween.from + this.tween.delta * (1 - Math.pow(1 - progress, 3));
        if (progress === 1) this.tween = null;
        moving = true;
      } else if (!this.drag && Math.abs(this.inertia) > 2) {
        this.phase += this.inertia * dt / 1000;
        this.inertia *= Math.exp(-dt / 240);
        moving = true;
      } else if (!blocked && time >= this.resumeAt) {
        this.inertia = 0;
        this.speed += (24 - this.speed) * (1 - Math.exp(-dt / 320));
        this.phase += this.speed * dt / 1000;
        moving = true;
      } else this.speed = 0;
      if (moving) this.draw();
      this.classList.toggle('is-moving', moving);
      if (moving || (!blocked && time < this.resumeAt)) this.raf = requestAnimationFrame(this.frame);
      else this.lastTime = 0;
    }

    wake() {
      if (!this.abort || this.raf || !this.visible || document.hidden || this.hidden) return;
      this.raf = requestAnimationFrame(this.frame);
    }

    sleep() {
      cancelAnimationFrame(this.raf);
      this.raf = 0; this.lastTime = 0; this.speed = 0; this.inertia = 0; this.tween = null;
      this.classList.remove('is-moving');
    }

    step(direction) {
      this.inertia = 0; this.speed = 0;
      this.resumeAt = performance.now() + 1800;
      if (this.media.matches) { this.phase += direction * this.stepWidth; this.draw(); }
      else this.tween = { from: this.phase, delta: direction * this.stepWidth, start: performance.now() };
      this.wake();
    }

    pointerDown(event) {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
      if (event.target.closest('[data-offer-clone]') && event.target.closest('a, button')) event.preventDefault();
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX, time: performance.now(), phase: this.phase, locked: false, velocity: 0 };
      this.speed = 0; this.inertia = 0; this.tween = null;
    }

    pointerMove(event) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.locked) {
        if (Math.abs(dy) > 7 && Math.abs(dy) > Math.abs(dx)) { this.drag = null; this.wake(); return; }
        if (Math.abs(dx) < 7 || Math.abs(dx) <= Math.abs(dy)) return;
        drag.locked = true;
        if (event.pointerType === 'mouse') getSelection()?.removeAllRanges();
        this.viewport.setPointerCapture(event.pointerId);
        this.classList.add('is-dragging');
      }
      event.preventDefault();
      const now = performance.now(), elapsed = Math.max(1, now - drag.time);
      const velocity = (drag.lastX - event.clientX) * this.direction * 1000 / elapsed;
      drag.velocity = Math.max(-1200, Math.min(1200, drag.velocity * .35 + velocity * .65));
      drag.lastX = event.clientX; drag.time = now;
      this.phase = drag.phase - dx * this.direction;
      this.draw();
    }

    pointerEnd(event, cancelled = false) {
      const drag = this.drag;
      if (!drag || drag.id !== event.pointerId) return;
      this.drag = null;
      if (drag.locked) {
        this.suppressClickUntil = performance.now() + 450;
        this.inertia = cancelled || this.media.matches || performance.now() - drag.time > 90 ? 0 : drag.velocity;
      }
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      this.classList.remove('is-dragging');
      this.resumeAt = performance.now() + 1800;
      this.wake();
    }

    setMotionLabel() {
      this.motion.setAttribute('aria-label', this.paused ? this.motion.dataset.play : this.motion.dataset.pause);
      this.motion.querySelector('[data-motion-play]').toggleAttribute('hidden', !this.paused);
      this.motion.querySelector('[data-motion-pause]').toggleAttribute('hidden', this.paused);
    }

    async copyCode(button) {
      this.resumeAt = performance.now() + 2400;
      try {
        if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(button.dataset.copyCode);
        this.status.textContent = this.dataset.copied.replace('__CODE__', button.dataset.copyCode);
      } catch {
        const range = document.createRange(); range.selectNodeContents(button.previousElementSibling);
        const selection = getSelection(); selection.removeAllRanges(); selection.addRange(range);
        this.status.textContent = this.dataset.copyFailed;
      }
      this.status.classList.remove('visually-hidden'); this.status.classList.add('rdb-offers__terms');
      this.wake();
    }

    checkExpiry() {
      if (Date.now() >= Date.parse(this.dataset.end)) { this.hidden = true; this.sleep(); }
    }

    disconnectedCallback() {
      this.sleep(); this.abort?.abort(); this.abort = null;
      this.resize?.disconnect(); this.intersection?.disconnect(); clearInterval(this.timer);
      this.querySelectorAll('[data-offer-clone]').forEach(e => e.remove());
      this.classList.remove('is-looping', 'is-dragging');
      this.list?.style.removeProperty('transform');
      this.style.removeProperty('--offer-width');
      this.measuredWidth = null;
    }
  });
}
